import { IFlightServiceLeg } from 'features/salesOrder/types';
// @ts-ignore - El archivo puede ser muy grande y causar problemas de bundling
import { AIRPORTS } from './airportsLatLon';

export interface ItineraryEmissionResult {
	totalDistanceKm: number;
	economyCo2Kg: number;
	businessCo2Kg: number;
}

// Factor base de emisiones: 0.115 kg CO₂ / pasajero-km
const BASE_CO2_FACTOR = 0.115;

// Multiplicadores por cabina
const CABIN_MULTIPLIERS = {
	economy: 1.0,
	business: 1.6,
} as const;

/**
 * Calcula la distancia entre dos puntos geográficos usando la fórmula de Haversine
 * @param lat1 Latitud del primer punto
 * @param lon1 Longitud del primer punto
 * @param lat2 Latitud del segundo punto
 * @param lon2 Longitud del segundo punto
 * @returns Distancia en kilómetros
 */
function calculateHaversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
	const R = 6371; // Radio de la Tierra en kilómetros
	const dLat = ((lat2 - lat1) * Math.PI) / 180;
	const dLon = ((lon2 - lon1) * Math.PI) / 180;
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
}

/**
 * Estima las emisiones de CO₂ para un itinerario completo
 * @param flightLegs Array de tramos de vuelo
 * @returns Resultado con distancia total y emisiones para economy y business, o null si no se puede calcular
 */
export function estimateItineraryEmissions(flightLegs: IFlightServiceLeg[]): ItineraryEmissionResult | null {
	try {
		if (!flightLegs || flightLegs.length === 0) {
			return null;
		}

		// Validar que AIRPORTS esté disponible
		if (!AIRPORTS || typeof AIRPORTS !== 'object') {
			return null;
		}

		let totalDistanceKm = 0;

		// Calcular distancia total sumando todos los tramos
		for (const leg of flightLegs) {
			const departureCode = leg.departure?.airport?.code?.toUpperCase()?.trim();
			const arrivalCode = leg.arrival?.airport?.code?.toUpperCase()?.trim();

			if (!departureCode || !arrivalCode) {
				return null; // Si falta algún código IATA, no podemos calcular
			}

			// Validar nuevamente que AIRPORTS esté disponible antes de acceder
			if (!AIRPORTS || typeof AIRPORTS !== 'object') {
				return null;
			}

			const departureAirport = AIRPORTS[departureCode];
			const arrivalAirport = AIRPORTS[arrivalCode];

			if (!departureAirport || !arrivalAirport) {
				return null; // Si algún aeropuerto no existe en el mapa, no podemos calcular
			}

			const legDistance = calculateHaversineDistance(
				departureAirport.lat,
				departureAirport.lon,
				arrivalAirport.lat,
				arrivalAirport.lon,
			);

			totalDistanceKm += legDistance;
		}

		// Calcular emisiones para economy y business
		const economyCo2Kg = totalDistanceKm * BASE_CO2_FACTOR * CABIN_MULTIPLIERS.economy;
		const businessCo2Kg = totalDistanceKm * BASE_CO2_FACTOR * CABIN_MULTIPLIERS.business;

		return {
			totalDistanceKm,
			economyCo2Kg,
			businessCo2Kg,
		};
	} catch (error) {
		// Si hay cualquier error (por ejemplo, AIRPORTS no está disponible), retornar null
		console.warn('Error al calcular emisiones de CO₂:', error);
		return null;
	}
}
